import React, { useState, useEffect, useCallback } from 'react'
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Grid,
  LinearProgress, Paper, TextField, Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import SyncIcon from '@material-ui/icons/Sync'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import axios from 'axios'

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2, 3) },
  card: { marginBottom: theme.spacing(2) },
  tokenField: { marginTop: theme.spacing(1), marginBottom: theme.spacing(1) },
  syncBtn: { marginTop: theme.spacing(1) },
  progressBox: { marginTop: theme.spacing(2) },
  statChip: { margin: theme.spacing(0.5) },
  errorList: { maxHeight: 200, overflow: 'auto', marginTop: theme.spacing(1) }
}))

const DEFAULT_CATEGORIES = [
  { path: 'products/irrigation', label: 'Irrigation' },
  { path: 'products/landscape-lighting', label: 'Landscape Lighting' },
  { path: 'products/landscape-supplies', label: 'Landscape Supplies' },
  { path: 'products/outdoor-living', label: 'Outdoor Living' }
]

export default function EwingSyncPanel () {
  const classes = useStyles()
  const [token, setToken] = useState('')
  const [selectedCategories, setSelectedCategories] = useState(['products/irrigation'])
  const [syncing, setSyncing] = useState(false)
  const [syncStatus, setSyncStatus] = useState(null)
  const [error, setError] = useState(null)
  const [polling, setPolling] = useState(false)

  const fetchStatus = useCallback(async () => {
    try {
      // Cache-bust to avoid 304s during active sync
      const res = await axios.get('/api/vendor-catalogs/ewing/sync-status', {
        params: { _t: Date.now() }
      })
      setSyncStatus(res.data)
      if (res.data && res.data.status === 'processing') setPolling(true)
      return res.data
    } catch (err) {
      console.error('Failed to fetch sync status:', err)
      return null
    }
  }, [])

  useEffect(() => { fetchStatus() }, [fetchStatus])

  useEffect(() => {
    if (!polling) return
    const interval = setInterval(async () => {
      const status = await fetchStatus()
      if (status && status.status !== 'processing') setPolling(false)
    }, 3000)
    return () => clearInterval(interval)
  }, [polling, fetchStatus])

  const toggleCategory = (path) => {
    setSelectedCategories(prev =>
      prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
    )
  }

  const handleSync = async (fresh = false) => {
    if (selectedCategories.length === 0) return
    setSyncing(true)
    setError(null)
    try {
      const body = { categories: selectedCategories }
      const normalizedToken = token.trim().replace(/^Bearer\s+/i, '')
      if (normalizedToken) body.token = normalizedToken
      if (fresh) body.fresh = true
      await axios.post('/api/vendor-catalogs/ewing/sync', body)
      setPolling(true)
      await fetchStatus()
    } catch (err) {
      setError(err.response?.data?.error || err.message)
    } finally {
      setSyncing(false)
    }
  }

  const [cancelling, setCancelling] = useState(false)

  const handleCancel = async () => {
    setCancelling(true)
    try {
      await axios.post('/api/vendor-catalogs/ewing/sync-cancel')
      await fetchStatus()
      setPolling(false)
    } catch (err) {
      setError(err.response?.data?.error || 'Cancel failed')
    } finally {
      setCancelling(false)
    }
  }

  const isProcessing = syncStatus?.status === 'processing'
  const isResumable = (syncStatus?.status === 'cancelled' || syncStatus?.status === 'failed') &&
    ((syncStatus?.config?.completedCategories?.length > 0) || syncStatus?.config?.resumeCategory)
  const progress = syncStatus?.progress || {}

  return (
    <div className={classes.root}>
      <Typography variant="h6" gutterBottom>Ewing Online Catalog Sync</Typography>

      <Card className={classes.card} variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Categories to Sync</Typography>
          <Box display="flex" flexWrap="wrap">
            {DEFAULT_CATEGORIES.map(cat => (
              <Chip
                key={cat.path}
                label={cat.label}
                color={selectedCategories.includes(cat.path) ? 'primary' : 'default'}
                onClick={() => toggleCategory(cat.path)}
                clickable
                className={classes.statChip}
              />
            ))}
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>Ewing Account Pricing</Typography>
            <Button
              variant="outlined"
              color="primary"
              href="https://www.ewingoutdoorsupply.com/customer/account/login/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ marginBottom: 8 }}
            >
              Open Ewing Login
            </Button>
            <TextField
              className={classes.tokenField}
              label="Ewing access_token (optional)"
              placeholder="Paste access_token after completing Ewing MFA"
              fullWidth
              variant="outlined"
              size="small"
              value={token}
              onChange={e => setToken(e.target.value)}
              helperText="MFA has to happen in Ewing. Paste the current access_token here to sync account pricing; leave blank for public pricing."
            />
          </Box>

          {isResumable && (
            <Alert severity="info" style={{ marginBottom: 8 }}>
              Previous sync was {syncStatus.status} at {syncStatus.config.resumeCategory || 'between categories'}
              {syncStatus.config.resumePage ? ` (page ${syncStatus.config.resumePage})` : ''}.
              {' '}Starting a new sync will <strong>resume</strong> from where it left off.
              {syncStatus.config.completedCategories?.length > 0 &&
                ` (${syncStatus.config.completedCategories.length} categor${syncStatus.config.completedCategories.length === 1 ? 'y' : 'ies'} already done)`}
              {syncStatus.config.hasToken && !token.trim() &&
                ' Previous token will be reused — paste a new one to override.'}
            </Alert>
          )}
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Button
              className={classes.syncBtn}
              variant="contained"
              color="primary"
              startIcon={syncing || isProcessing ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
              onClick={() => handleSync(false)}
              disabled={syncing || isProcessing || selectedCategories.length === 0}
            >
              {isProcessing ? 'Syncing...' : isResumable ? 'Resume Sync' : 'Sync Ewing Catalog'}
            </Button>
            {isResumable && !isProcessing && (
              <Button
                className={classes.syncBtn}
                variant="outlined"
                onClick={() => handleSync(true)}
                disabled={syncing}
              >
                Start Fresh
              </Button>
            )}
            {isProcessing && (
              <Button
                className={classes.syncBtn}
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Sync'}
              </Button>
            )}
          </Box>

          {error && <Alert severity="error" style={{ marginTop: 8 }}>{error}</Alert>}
        </CardContent>
      </Card>

      {syncStatus && syncStatus.status !== 'none' && (
        <Card className={classes.card} variant="outlined">
          <CardContent>
            <Box display="flex" alignItems="center" mb={1}>
              {syncStatus.status === 'completed' && <CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} />}
              {(syncStatus.status === 'failed' || syncStatus.status === 'cancelled') && <ErrorIcon color={syncStatus.status === 'cancelled' ? 'action' : 'error'} style={{ marginRight: 8 }} />}
              {syncStatus.status === 'processing' && <CircularProgress size={18} style={{ marginRight: 8 }} />}
              <Typography variant="subtitle2">
                Last Sync: {syncStatus.status} {syncStatus.completedAt ? `— ${new Date(syncStatus.completedAt).toLocaleString()}` : ''}
              </Typography>
            </Box>

            {isProcessing && progress.totalProducts > 0 && (
              <Box className={classes.progressBox}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((progress.processed / progress.totalProducts) * 100, 100)}
                />
                <Typography variant="caption">
                  {progress.processed} / {progress.totalProducts} products
                </Typography>
                {progress.currentMessage && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    {progress.currentMessage}
                    {progress.currentCategory ? ` — ${progress.currentCategory}` : ''}
                    {progress.currentPage ? ` page ${progress.currentPage}${progress.totalPages ? ` of ${progress.totalPages}` : ''}` : ''}
                    {progress.currentSku ? ` — ${progress.currentSku}` : ''}
                  </Typography>
                )}
                {progress.lastActivityAt && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    Last activity: {new Date(progress.lastActivityAt).toLocaleTimeString()}
                  </Typography>
                )}
              </Box>
            )}

            <Grid container spacing={1} style={{ marginTop: 8 }}>
              <Grid item><Chip size="small" label={`Created: ${progress.created || 0}`} className={classes.statChip} /></Grid>
              <Grid item><Chip size="small" label={`Updated: ${progress.updated || 0}`} className={classes.statChip} /></Grid>
              <Grid item><Chip size="small" label={`Unchanged: ${progress.unchanged || 0}`} className={classes.statChip} /></Grid>
              <Grid item><Chip size="small" label={`Errors: ${progress.errors || 0}`} color={progress.errors > 0 ? 'secondary' : 'default'} className={classes.statChip} /></Grid>
            </Grid>

            {syncStatus.status === 'completed' && progress.created === 0 && progress.updated === 0 && progress.processed > 0 && (
              <Alert severity="warning" style={{ marginTop: 8 }}>
                Sync completed with 0 new products ({progress.unchanged} unchanged).
                The token may have expired — try logging into Ewing again and pasting a fresh token.
              </Alert>
            )}

            {syncStatus.config?.authenticated && (
              <Chip size="small" label="Account Pricing" color="primary" style={{ marginTop: 4 }} />
            )}
            {syncStatus.resumedFromJobId && (
              <Chip size="small" label="Resumed" style={{ marginTop: 4, marginLeft: 4 }} />
            )}

            {syncStatus.errorLog && syncStatus.errorLog.length > 0 && (
              <Paper variant="outlined" className={classes.errorList}>
                {syncStatus.errorLog.slice(-10).map((e, i) => (
                  <Typography key={i} variant="caption" display="block" style={{ padding: '2px 8px' }}>
                    {e.sku && `[${e.sku}] `}{e.message}
                  </Typography>
                ))}
              </Paper>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
